import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import ContactUs from '../Help/ContactUs';
import { helpUrls, HelpAction } from '../../lib/helpUrls';
import packageJson from '../../../package.json';

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { isDark, themeMode, setThemeMode } = useTheme();
  const [showContactUs, setShowContactUs] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const handleHelpAction = (action: HelpAction) => {
    switch (action) {
      case 'rulesPractices':
        window.open(helpUrls.termsAndConditions, '_blank', 'noopener,noreferrer');
        break;
      case 'patientPrivacy':
        window.open(helpUrls.patientPrivacy, '_blank', 'noopener,noreferrer');
        break;
      case 'communityGuidelines':
        window.open(helpUrls.communityGuidelines, '_blank', 'noopener,noreferrer');
        break;
      case 'faq':
        window.open(helpUrls.faq, '_blank', 'noopener,noreferrer');
        break;
      case 'contactUs':
        setShowContactUs(true);
        break;
      case 'aboutChime':
        window.open(helpUrls.aboutChime, '_blank', 'noopener,noreferrer');
        break;
      case 'aboutAdvita':
        window.open(helpUrls.aboutAdvita, '_blank', 'noopener,noreferrer');
        break;
      default:
        console.log('Unknown help action:', action);
    }
  };


  const appVersion = packageJson.version;

  return (
    <div className="max-w-4xl mx-auto p-6 theme-surface">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold theme-text">Settings</h1>
        <button
          onClick={() => navigate('/')}
          className="theme-text-secondary hover:theme-text-primary p-2 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-8">
        {/* Account Section */}
        <div>
          <h2 className="text-lg font-semibold theme-text mb-4">Account</h2>
          <div className="theme-surface border rounded-lg overflow-hidden">
            <SettingsItem
              title="Joint Specialty"
              type="navigation"
              onClick={() => navigate('/settings/joint-specialty')}
            />
            <SettingsItem
              title="Notification Settings"
              type="navigation"
              onClick={() => navigate('/settings/notifications')}
            />
            <SettingsItem
              title={`Theme: ${themeMode === 'auto' ? 'Auto (System)' : themeMode === 'light' ? 'Light' : 'Dark'}`}
              type="navigation"
              onClick={() => {
                // Cycle through modes
                let newMode: 'light' | 'dark' | 'auto';
                if (themeMode === 'auto') {
                  newMode = 'light';
                } else if (themeMode === 'light') {
                  newMode = 'dark';
                } else {
                  newMode = 'auto';
                }
                setThemeMode(newMode);
              }}
            />
          </div>
        </div>

        {/* App Information Section */}
        <div>
          <h2 className="text-lg font-semibold theme-text mb-4">App Information</h2>
          <div className="theme-surface border rounded-lg overflow-hidden">
            <SettingsItem
              title="App Information"
              type="navigation"
              onClick={() => navigate('/settings/app-information')}
            />
            <SettingsItem
              title="Help & Support"
              type="navigation"
              onClick={() => navigate('/settings/help-support')}
            />
          </div>
        </div>

        {/* Sign Out Section */}
        <div>
          <div className="theme-surface border rounded-lg overflow-hidden">
            <SettingsItem
              title="Sign Out"
              type="button"
              onClick={handleLogout}
              isDestructive={false}
            />
          </div>
        </div>

        {/* Delete Account Section - Separated with more space */}
        <div className="pb-8">
          <div className="theme-surface border rounded-lg overflow-hidden">
            <SettingsItem
              title="Delete Account"
              type="button"
              onClick={() => {
                // TODO: Implement delete account
                console.log('Delete account');
              }}
              isDestructive={true}
            />
          </div>
        </div>
      </div>

      {/* Help & Support Modals */}
      {showContactUs && (
        <ContactUs onClose={() => setShowContactUs(false)} />
      )}

    </div>
  );
}

interface SettingsItemProps {
  title: string;
  type: 'navigation' | 'switch' | 'text' | 'button';
  isEnabled?: boolean;
  onToggle?: (value: boolean) => void | (() => void);
  onClick?: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
}

function SettingsItem({
  title,
  type,
  isEnabled = false,
  onToggle,
  onClick,
  isDestructive = false,
  isLoading = false
}: SettingsItemProps) {
  const content = (
    <>
      <span className={`text-sm font-medium ${
        isDestructive ? 'text-red-600' : 'theme-text-primary'
      }`}>
        {title}
      </span>

      {type === 'switch' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onToggle === 'function') {
              // Handle both types: (value: boolean) => void and () => void
              if (onToggle.length === 0) {
                (onToggle as () => void)();
              } else {
                (onToggle as (value: boolean) => void)(!isEnabled);
              }
            }
          }}
          disabled={isLoading}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-healthy-green focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            isEnabled ? 'bg-healthy-green' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform flex items-center justify-center ${
              isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          >
            {isLoading && (
              <div className="w-2 h-2 border theme-border-secondary border-t-theme-text-secondary rounded-full animate-spin"></div>
            )}
          </span>
        </button>
      )}

      {(type === 'navigation' || type === 'button') && (
        <svg className="w-5 h-5 theme-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </>
  );

  // For navigation and button types, make entire row clickable
  if (type === 'navigation' || type === 'button') {
    return (
      <button
        onClick={onClick}
        className={`w-full px-4 py-3 border-b theme-border-primary last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
          type === 'button' && isDestructive ? 'hover:bg-red-50 dark:hover:bg-red-900/10' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          {content}
        </div>
      </button>
    );
  }

  // For switch type, keep as div (switch itself is clickable)
  return (
    <div className="px-4 py-3 border-b theme-border-primary last:border-b-0">
      <div className="flex items-center justify-between">
        {content}
      </div>
    </div>
  );
}